/**
 * atomberg-installation-service.js
 *
 * Accordion toggle + auto-expand-on-check for the installation-service card.
 * Event delegation survives DOM morphing without rebinding.
 *
 * PDP merge-prevention:
 * A hidden input (<input data-atomberg-installation-prop>) always submits a
 * defined _is_installation_request value (true or false). Before the product
 * form submits, wireFormSync() syncs that hidden input to the existing cart
 * line's value so adding the same variant always produces matching properties
 * and Shopify merges the lines instead of creating a duplicate.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergInstallationInit) return;
  w._atombergInstallationInit = true;

  // ── Cart cache ────────────────────────────────────────────────────────────
  // Kept fresh after every cart:update so the form submit interceptor can read
  // the current cart state synchronously (no blocking async in the submit handler).

  /** @type {{ items: Array<{ variant_id: number, properties: Record<string,string> }> } | null} */
  var cachedCart = null;

  function fetchCartCache() {
    var shared = w.AtombergCartState;
    var req = shared
      ? shared.getCart({ fresh: true })
      : fetch('/cart.js').then(function (r) {
          return r.json();
        });
    req
      .then(function (/** @type {any} */ c) {
        cachedCart = c;
      })
      .catch(function () {});
  }

  fetchCartCache();

  document.addEventListener('cart:update', function () {
    // defer until after Horizon's cart update has committed
    setTimeout(fetchCartCache, 0);
  });

  // ── Accordion toggle ──────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    var target = /** @type {Element} */ (e.target);
    var toggle = /** @type {HTMLElement | null} */ (
      target.closest('[data-atomberg-installation-toggle]')
    );
    if (!toggle) return;

    var root = toggle.closest('[data-atomberg-installation]');
    if (!root) return;

    var body = root.querySelector('[data-atomberg-installation-body]');
    if (!body) return;

    var open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    if (open) {
      body.setAttribute('hidden', '');
    } else {
      body.removeAttribute('hidden');
    }
  });

  // ── Checkbox → hidden input sync + auto-expand ────────────────────────────

  document.addEventListener('change', function (e) {
    var target = /** @type {Element} */ (e.target);
    var cb = /** @type {HTMLInputElement | null} */ (
      target.closest('[data-atomberg-installation-checkbox]')
    );
    if (!cb) return;

    var root = cb.closest('[data-atomberg-installation]');
    if (!root) return;

    var propInput = /** @type {HTMLInputElement | null} */ (
      root.querySelector('[data-atomberg-installation-prop]')
    );
    // Keep hidden input in sync so both states produce a defined property value
    if (propInput) propInput.value = cb.checked ? 'true' : 'false';

    var toggle = root.querySelector('[data-atomberg-installation-toggle]');
    var body = root.querySelector('[data-atomberg-installation-body]');
    if (cb.checked && toggle && body && toggle.getAttribute('aria-expanded') !== 'true') {
      toggle.setAttribute('aria-expanded', 'true');
      body.removeAttribute('hidden');
    }
  });

  // ── Form submit interceptor (PDP only) ────────────────────────────────────

  function wireFormSync() {
    var propInput = /** @type {HTMLInputElement | null} */ (
      document.querySelector('[data-atomberg-installation-prop]')
    );
    if (!propInput) return;

    var formId = propInput.getAttribute('form');
    if (!formId) return;

    var form = /** @type {HTMLFormElement | null} */ (document.getElementById(formId));
    if (!form) return;

    /** @type {any} */
    var anyForm = form;
    if (anyForm._atombergFormSyncBound) return;
    anyForm._atombergFormSyncBound = true;

    var boundForm = form;

    // Capture phase — runs before Horizon's submit handler reads form values
    boundForm.addEventListener(
      'submit',
      function () {
        if (!cachedCart) return;

        var variantInput = /** @type {HTMLInputElement | null} */ (
          boundForm.querySelector('[name="id"]')
        );
        if (!variantInput) return;

        var variantId = variantInput.value;
        var existingLine = cachedCart.items.find(function (item) {
          return String(item.variant_id) === String(variantId);
        });

        if (!existingLine) return;

        // Inherit the existing line's installation state so properties match → merge
        var existingValue =
          (existingLine.properties && existingLine.properties._is_installation_request) || 'false';

        var prop = /** @type {HTMLInputElement | null} */ (
          document.querySelector('[data-atomberg-installation-prop]')
        );
        if (prop) prop.value = existingValue;

        var checkbox = /** @type {HTMLInputElement | null} */ (
          document.querySelector('[data-atomberg-installation-checkbox]')
        );
        if (checkbox) checkbox.checked = existingValue === 'true';
      },
      true
    ); // capture phase — before Horizon reads form values
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireFormSync);
  } else {
    wireFormSync();
  }

  document.addEventListener('shopify:section:load', wireFormSync);
  document.addEventListener('shopify:block:load', wireFormSync);
})();
